import type { KgDebugEntry } from './types'

/** Short enough to sit on one timeline row. */
const MAX_SUMMARY_CHARS = 140

type Obj = Record<string, unknown>

function obj(value: unknown): Obj {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Obj)
    : {}
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}

function count(value: unknown): number | undefined {
  return Array.isArray(value) ? value.length : undefined
}

function clip(text: string): string {
  const one = text.replace(/\s+/g, ' ').trim()
  return one.length > MAX_SUMMARY_CHARS
    ? `${one.slice(0, MAX_SUMMARY_CHARS - 1)}…`
    : one
}

/** The timeline row's label: the stage, or the model turn's purpose, with its leg when split. */
export function entryTitle(entry: KgDebugEntry): string {
  if (entry.kind === 'model') return `${entry.purpose} (model)`
  return entry.leg === undefined ? entry.stage : `${entry.stage} · ${entry.leg}`
}

/**
 * One line saying what the entry found, read from the fields the engine reports for each stage.
 * The shapes belong to the engine and move with it, so every field is optional here: an unknown
 * or reshaped step falls back to its status rather than breaking the page.
 */
export function entrySummary(entry: KgDebugEntry): string {
  if (entry.kind === 'model') {
    if (entry.error !== undefined) return clip(`failed: ${entry.error}`)
    return clip(entry.output ?? '(no reply)')
  }
  const input = obj(entry.input)
  const output = obj(entry.output)
  switch (entry.stage) {
    case 'understand': {
      const spans = Array.isArray(output.span_list) ? output.span_list : []
      const names = spans
        .map(
          (s) =>
            str(obj(s).text) ??
            str(obj(s).span) ??
            (typeof s === 'string' ? s : undefined)
        )
        .filter((s): s is string => s !== undefined)
      const source = str(output.source) ?? 'unknown'
      const reason = str(output.reason)
      return clip(
        `${spans.length} span(s)${names.length > 0 ? `: ${names.join(', ')}` : ''} · source=${source}` +
          (reason === undefined ? '' : ` (${reason})`)
      )
    }
    case 'validate': {
      if (output.ok === true) {
        const repairs = count(output.slot_repairs)
        return clip(
          `${str(output.query) ?? str(input.line) ?? 'ok'}${repairs ? ` · ${repairs} repair(s)` : ''}`
        )
      }
      return clip(
        `rejected: ${str(output.reason) ?? 'no reason'} · line ${str(input.line) ?? '?'}`
      )
    }
    case 'canonicalize': {
      const span = str(input.span) ?? '?'
      const verdict = str(output.verdict) ?? entry.status
      // `entity` is the resolved id; `candidates` is a count on the sheet-scoped verdicts and the
      // list itself on the resolver's own.
      const entity = str(output.entity) ?? str(obj(output.entity).entityId)
      const candidates =
        typeof output.candidates === 'number'
          ? output.candidates
          : count(output.candidates)
      const reason = str(output.reason)
      return clip(
        `${span} → ${verdict}` +
          (entity === undefined ? '' : ` ${entity}`) +
          (candidates === undefined ? '' : ` · ${candidates} candidate(s)`) +
          (reason === undefined ? '' : ` (${reason})`)
      )
    }
    case 'execute': {
      const queries = count(output.queries)
      const rendered = str(output.rendered)
      const empty = str(output.empty_because)
      return clip(
        `${queries ?? 0} SQL read(s)` +
          (rendered === undefined
            ? ''
            : ` · evidence ${rendered.length} chars`) +
          (empty === undefined ? '' : ` · empty: ${empty}`)
      )
    }
    case 'cdoc': {
      const hits = count(output.hits)
      return clip(
        `${hits ?? 0} passage(s) · ${output.rendered_chars ?? 0} chars rendered`
      )
    }
    default:
      return entry.status === 'error' ? 'error' : 'ok'
  }
}

/** The whole run as one JSON file, for sharing or loading into a notebook. */
export function downloadName(question: string, startedAt: number): string {
  const slug = question
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40)
  return `kg-debug-${slug || 'run'}-${startedAt}.json`
}

/** Comma- or newline-separated names, blanks dropped; undefined when none are left. */
export function splitNames(text: string): string[] | undefined {
  const names = text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s !== '')
  return names.length === 0 ? undefined : names
}
